// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {Space, Styled, Icon} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {usePopover} from '@supermove/hooks';
import {OrganizationModel, ProjectModel, Stage} from '@supermove/models';
import {colors} from '@supermove/styles';
import {ViewStyleProp} from '@supermove/styles/types';

// App
import ActionMenuPopover from '@shared/design/components/ActionMenu/ActionMenuPopover';
import Badge from '@shared/design/components/Badge';
import ProjectStatus from '@shared/modules/Project/enums/ProjectStatus';
import ProjectStageForm from '@shared/modules/Project/forms/ProjectStageForm';
import useUpdateProjectSalesStageMutation from '@shared/modules/Project/hooks/useUpdateProjectSalesStageMutation';

const BadgeDropdownContainer = Styled.View<{maxWidth?: number}>`
  max-width: ${({maxWidth}) => (maxWidth ? `${maxWidth}px` : '100%')};
`;

const SalesStageButton = Styled.ButtonV2`
`;

const ProjectSalesStageBadgeDropdown = ({
  project,
  organization,
  dropdownWidth = 240,
  badgeMaxWidth,
  isEditable = false,
  containerStyle,
  badgeStyle,
}: {
  project: ProjectModel;
  organization: OrganizationModel;
  dropdownWidth?: number;
  badgeMaxWidth?: number;
  isEditable?: boolean;
  containerStyle?: ViewStyleProp;
  badgeStyle?: ViewStyleProp;
}) => {
  const salesStagesPopover = usePopover();
  const projectStageForm = ProjectStageForm.new({
    projectId: project.id,
    stageId: project.salesStage.id,
  });
  const {form, handleSubmit, submitting} = useUpdateProjectSalesStageMutation({
    projectStageForm,
    onSuccess: salesStagesPopover.handleClose,
    onError: (errors) => console.log({errors}),
  });

  const orderedStages = Stage.getOrderedStages({
    stages: organization.companySettings.salesStages,
  });
  const isEnabled = isEditable && _.includes(ProjectStatus.ACTIVE_STATUSES, project.status);

  return (
    <BadgeDropdownContainer maxWidth={badgeMaxWidth} style={containerStyle}>
      <ActionMenuPopover
        popover={salesStagesPopover}
        width={dropdownWidth}
        actions={orderedStages.map((stage) => {
          return {
            text: (
              <React.Fragment>
                <Icon source={Icon.CircleSmall} color={stage.color} size={16} />
                <Space width={8} />
                {stage.name}
              </React.Fragment>
            ),
            onPress: () => {
              form.setFieldValue('projectStageForm.stageId', stage.id);
              setTimeout(handleSubmit, 0);
            },
            isDisabled: submitting || _.toString(stage.id) === _.toString(project.salesStage.id),
          };
        })}
      >
        <SalesStageButton onPress={salesStagesPopover.handleToggle} isDisabled={!isEnabled}>
          <Badge
            label={project.salesStage.name}
            color={colors.white}
            backgroundColor={project.salesStage.color}
            iconRight={isEnabled ? Icon.AngleDown : undefined}
            numberOfLines={1}
            style={badgeStyle}
            isSubmitting={submitting}
            isResponsive
          />
        </SalesStageButton>
      </ActionMenuPopover>
    </BadgeDropdownContainer>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
ProjectSalesStageBadgeDropdown.projectFragment = gql`
  fragment ProjectSalesStageBadgeDropdown_Project on Project {
    id
    status
    salesStage {
      id
      name
      color
    }
  }
`;

ProjectSalesStageBadgeDropdown.organizationFragment = gql`
  ${Stage.getOrderedStages.fragment}

  fragment ProjectSalesStageBadgeDropdown_Organization on Organization {
    id
    companySettings {
      salesStages {
        id
        name
        color
        ...Stage_getOrderedStages
      }
    }
  }
`;

export default ProjectSalesStageBadgeDropdown;
